import cloneDeep from 'lodash/cloneDeep';
import { useQuery, useQueryClient } from 'react-query';
import { DocketCaseDocument } from '../../models/CompanyDockets';
import { fetchDocumentAttachments } from '../../services/dockets/dockets.service';
import { queryKeys } from '../queryKeyFactory';
import { CaseDocumentsQueryFilters, UseFetchCaseDocumentQueryResult } from './useFetchCaseDocuments';

export interface UseFetchDocumentAttachmentsQuery {
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export default (
  docket: DocketCaseDocument,
  page: number,
  pageSize: number,
  filters: CaseDocumentsQueryFilters,
  keyFilings: boolean
): UseFetchDocumentAttachmentsQuery => {
  const queryClient = useQueryClient();
  const { docketItemCaseId, docketNumber, filedDate } = docket;

  const query = useQuery<DocketCaseDocument[], Error>(
    queryKeys.dockets.fetchDocuments.attachments(
      docketItemCaseId,
      docketNumber!,
      filedDate!,
      pageSize,
      filters,
      keyFilings
    ),
    async () => {
      const response = await fetchDocumentAttachments(docketItemCaseId, filedDate!);

      const filteredDocketItemList = response.filter(
        (item) => item.docketItemNumber === docketNumber && item.docketItemAttachmentNumber !== 0
      );
      return filteredDocketItemList.map((item) => ({
        docketItemCaseId: item.docketItemCaseId,
        docketItemId: item.docketItemId,
        description: item.docketItemText,
        link: item.docketItemPermalink,
        isAttachment: true,
        hasAttachments: false,
        docketNumber: item.docketItemNumber,
        filedDate: item.docketItemFiledDate,
        fileAvailable: !!item.docketItemFileAvailable,
        canAccess: item.canAccess,
      }));
    },
    {
      enabled: !Number.isNaN(Number(docketItemCaseId)),
      onSuccess: (data) => {
        const caseDocuments = cloneDeep(
          queryClient.getQueryData<UseFetchCaseDocumentQueryResult>(
            queryKeys.dockets.fetchDocuments.query(docketItemCaseId, page, pageSize, filters, keyFilings)
          )
        );
        const docketItem = caseDocuments?.result.find((document) => document.docketNumber === docketNumber);
        if (docketItem) {
          Object.assign(docketItem, { subRows: data });
        }
        queryClient.setQueryData(
          queryKeys.dockets.fetchDocuments.query(docketItemCaseId, page, pageSize, filters, keyFilings),
          caseDocuments
        );
      },
    }
  );

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};
